/**
 * @fileoverview Zod schema for the organisation details form.
 * @description Mirrors the backend's Update Organisation Details rules: a legal name and the six
 * bank fields are required; every other field is optional and, when filled, must match its pattern
 * (CIN, GSTIN, PAN, pincode, phone, email). Codes are checked case-insensitively and upper-cased
 * when saved. Messages are translation keys, translated only at the display call site in
 * OrganisationDetailsForm.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { z } from 'zod'

/** @description The longest legal name, trade name or beneficiary name the backend accepts. */
const NAME_MAX = 200

/**
 * @description A text field that may be left blank but, when filled, must match a pattern.
 * @param {RegExp} pattern - What a filled value must match.
 * @param {string} message - The translation key shown when it does not.
 * @returns {z.ZodString} The field's rule.
 */
function optionalPattern(pattern: RegExp, message: string) {
  return z
    .string()
    .trim()
    .refine((value) => value === '' || pattern.test(value), message)
}

/**
 * @description A text field that may be left blank but not exceed a length.
 * @param {number} max - The longest allowed value.
 * @returns {z.ZodString} The field's rule.
 */
function optionalText(max: number) {
  return z.string().trim().max(max, 'sales.myWork.organisation.errors.tooLong')
}

/**
 * @description A required text field.
 * @param {number} max - The longest allowed value.
 * @returns {z.ZodString} The field's rule.
 */
function requiredText(max: number) {
  return z
    .string()
    .trim()
    .min(1, 'sales.myWork.organisation.errors.required')
    .max(max, 'sales.myWork.organisation.errors.tooLong')
}

/** @description The organisation details form's rules. */
export const organisationDetailsFormSchema = z.object({
  legalName: requiredText(NAME_MAX),
  tradeName: optionalText(NAME_MAX),
  cin: optionalPattern(
    /^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/i,
    'sales.myWork.organisation.errors.cin',
  ),
  gstin: optionalPattern(
    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/i,
    'sales.myWork.organisation.errors.gstin',
  ),
  pan: optionalPattern(/^[A-Z]{5}[0-9]{4}[A-Z]$/i, 'sales.myWork.organisation.errors.pan'),
  registeredAddress: optionalText(500),
  city: optionalText(100),
  state: optionalText(100),
  pincode: optionalPattern(/^[1-9][0-9]{5}$/, 'sales.myWork.organisation.errors.pincode'),
  billingContactName: optionalText(150),
  billingEmail: optionalPattern(
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    'sales.myWork.organisation.errors.email',
  ).max(320, 'sales.myWork.organisation.errors.tooLong'),
  billingPhone: optionalPattern(/^[0-9+() -]{5,25}$/, 'sales.myWork.organisation.errors.phone'),
  supportEmail: optionalPattern(
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    'sales.myWork.organisation.errors.email',
  ).max(320, 'sales.myWork.organisation.errors.tooLong'),
  bank: z.object({
    beneficiaryName: requiredText(NAME_MAX),
    bankName: requiredText(150),
    branch: requiredText(150),
    accountNumber: z
      .string()
      .trim()
      .regex(/^[0-9]{9,18}$/, 'sales.myWork.organisation.errors.accountNumber'),
    ifsc: z
      .string()
      .trim()
      .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/i, 'sales.myWork.organisation.errors.ifsc'),
    accountType: z.enum(['CURRENT', 'SAVINGS']),
  }),
})

/** @description The organisation details form's values. */
export type OrganisationDetailsFormValues = z.infer<typeof organisationDetailsFormSchema>
